import React, { useEffect, useState } from 'react'
import {
	Card,
	Switch,
	Typography,
	Spin,
	message,
	Row,
	Col,
	Space,
	Button,
	Alert,
	Tag,
	Divider,
	Select,
	Input,
	Tooltip,
} from 'antd'
import {
	SafetyCertificateOutlined,
	PhoneOutlined,
	GoogleOutlined,
	AppleOutlined,
	ReloadOutlined,
	SaveOutlined,
	CheckCircleOutlined,
	PlusOutlined,
} from '@ant-design/icons'
import { httpGet, httpPut } from '../../common/http.service'
import { useThemeColors } from '../../common/ThemeContext'

const { Title, Text, Paragraph } = Typography

export const DEFAULT_ALLOWED_PHONE_PREFIXES: string[] = [
	'010',
	'015',
	'016',
	'069',
	'070',
	'081',
	'086',
	'087',
	'089',
	'093',
	'096',
	'098',
]

const SUGGESTED_CAMBODIA_PREFIXES: { label: string; value: string }[] = [
	// Smart Axiata (Default)
	{ label: '010 (Smart)', value: '010' },
	{ label: '015 (Smart)', value: '015' },
	{ label: '016 (Smart)', value: '016' },
	{ label: '069 (Smart)', value: '069' },
	{ label: '070 (Smart)', value: '070' },
	{ label: '081 (Smart)', value: '081' },
	{ label: '086 (Smart)', value: '086' },
	{ label: '087 (Smart)', value: '087' },
	{ label: '093 (Smart)', value: '093' },
	{ label: '096 (Smart)', value: '096' },
	{ label: '098 (Smart)', value: '098' },
	// Cellcard
	{ label: '011 (Cellcard)', value: '011' },
	{ label: '012 (Cellcard)', value: '012' },
	{ label: '014 (Cellcard)', value: '014' },
	{ label: '017 (Cellcard)', value: '017' },
	{ label: '061 (Cellcard)', value: '061' },
	{ label: '076 (Cellcard)', value: '076' },
	{ label: '077 (Cellcard)', value: '077' },
	{ label: '078 (Cellcard)', value: '078' },
	{ label: '079 (Cellcard)', value: '079' },
	{ label: '085 (Cellcard)', value: '085' },
	{ label: '089 (Cellcard)', value: '089' },
	{ label: '092 (Cellcard)', value: '092' },
	{ label: '095 (Cellcard)', value: '095' },
	{ label: '099 (Cellcard)', value: '099' },
	// Metfone
	{ label: '031 (Metfone)', value: '031' },
	{ label: '060 (Metfone)', value: '060' },
	{ label: '066 (Metfone)', value: '066' },
	{ label: '067 (Metfone)', value: '067' },
	{ label: '068 (Metfone)', value: '068' },
	{ label: '071 (Metfone)', value: '071' },
	{ label: '088 (Metfone)', value: '088' },
	{ label: '090 (Metfone)', value: '090' },
	{ label: '097 (Metfone)', value: '097' },
]

const normalizePrefix = (raw: string): string => {
	let digits = raw.replace(/\D/g, '')
	if (!digits) return ''
	if (digits.startsWith('855')) {
		digits = digits.slice(3)
	}
	if (!digits.startsWith('0')) {
		digits = `0${digits}`
	}
	return digits
}

const normalizePrefixList = (list: string[]): string[] => {
	const seen = new Set<string>()
	const result: string[] = []
	for (const item of list) {
		const norm = normalizePrefix(String(item))
		if (norm.length >= 2 && norm.length <= 5 && !seen.has(norm)) {
			seen.add(norm)
			result.push(norm)
		}
	}
	if (result.join(',') === '010,015,016,069,070,081,086,087,093,096,098') {
		return [...DEFAULT_ALLOWED_PHONE_PREFIXES]
	}
	return result
}

const arePrefixListsEqual = (a: string[], b: string[]): boolean => {
	if (a.length !== b.length) return false
	return a.every((val, idx) => val === b[idx])
}

export interface AuthOptions {
	phoneLoginEnabled: boolean
	googleLoginEnabled: boolean
	appleLoginEnabled: boolean
	allowManualOtpEntry: boolean
	allowedPhonePrefixes: string[]
}

const DEFAULT_AUTH_OPTIONS: AuthOptions = {
	phoneLoginEnabled: true,
	googleLoginEnabled: true,
	appleLoginEnabled: true,
	allowManualOtpEntry: false,
	allowedPhonePrefixes: [...DEFAULT_ALLOWED_PHONE_PREFIXES],
}

export const AuthOptionsTab: React.FC = () => {
	const { primary } = useThemeColors()
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [rawSettings, setRawSettings] = useState<Record<string, any>>({})
	const [authOptions, setAuthOptions] = useState<AuthOptions>(DEFAULT_AUTH_OPTIONS)
	const [initialAuthOptions, setInitialAuthOptions] = useState<AuthOptions>(DEFAULT_AUTH_OPTIONS)
	const [customPrefixInput, setCustomPrefixInput] = useState('')

	useEffect(() => {
		loadSettings()
	}, [])

	const loadSettings = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/app-settings')
			if (response?.data) {
				setRawSettings(response.data)
				const incoming = response.data.authOptions || {}
				const incomingPrefixes = Array.isArray(incoming.allowedPhonePrefixes)
					? normalizePrefixList(incoming.allowedPhonePrefixes)
					: [...DEFAULT_ALLOWED_PHONE_PREFIXES]
				const loaded: AuthOptions = {
					phoneLoginEnabled: incoming.phoneLoginEnabled ?? true,
					googleLoginEnabled: incoming.googleLoginEnabled ?? true,
					appleLoginEnabled: incoming.appleLoginEnabled ?? true,
					allowManualOtpEntry: incoming.allowManualOtpEntry ?? false,
					allowedPhonePrefixes: incomingPrefixes,
				}
				setAuthOptions(loaded)
				setInitialAuthOptions({
					...loaded,
					allowedPhonePrefixes: [...loaded.allowedPhonePrefixes],
				})
			}
		} catch (error: any) {
			console.error('Failed to load authentication options:', error)
			message.error(error?.response?.data?.message || 'Failed to load authentication settings')
		} finally {
			setLoading(false)
		}
	}

	const handleToggle = (
		key: 'phoneLoginEnabled' | 'googleLoginEnabled' | 'appleLoginEnabled',
		value: boolean
	) => {
		setAuthOptions((prev) => ({
			...prev,
			[key]: value,
		}))
	}

	const handlePrefixesChange = (values: string[]) => {
		const normalized = normalizePrefixList(values)
		setAuthOptions((prev) => ({
			...prev,
			allowedPhonePrefixes: normalized,
		}))
	}

	const handleAddCustomPrefix = () => {
		const norm = normalizePrefix(customPrefixInput)
		if (!norm || norm.length < 2 || norm.length > 5) {
			message.warning('Please enter a valid 2 to 4 digit phone prefix (e.g. 012, 098)')
			return
		}
		if (authOptions.allowedPhonePrefixes.includes(norm)) {
			message.info(`Prefix ${norm} is already in the whitelist`)
			setCustomPrefixInput('')
			return
		}
		setAuthOptions((prev) => ({
			...prev,
			allowedPhonePrefixes: [...prev.allowedPhonePrefixes, norm],
		}))
		setCustomPrefixInput('')
	}

	const handleResetPrefixes = () => {
		setAuthOptions((prev) => ({
			...prev,
			allowedPhonePrefixes: [...DEFAULT_ALLOWED_PHONE_PREFIXES],
		}))
		message.info('Reset phone prefixes to default Smart Axiata prefixes')
	}

	const handleReset = () => {
		setAuthOptions({
			...DEFAULT_AUTH_OPTIONS,
			allowedPhonePrefixes: [...DEFAULT_ALLOWED_PHONE_PREFIXES],
		})
		message.info('Reset authentication options and phone prefixes to defaults')
	}

	const handleSave = async () => {
		const normalizedPrefixes = normalizePrefixList(authOptions.allowedPhonePrefixes)
		if (normalizedPrefixes.length === 0) {
			message.warning(
				'Please keep at least one allowed phone prefix in the whitelist, or click Reset Prefixes.'
			)
			return
		}
		try {
			setSaving(true)
			const payload = {
				...rawSettings,
				authOptions: {
					...authOptions,
					allowedPhonePrefixes: normalizedPrefixes,
				},
			}
			const response: any = await httpPut('/api/v1/admin/app-settings', payload)
			if (response?.data) {
				setRawSettings(response.data)
			}
			setInitialAuthOptions({
				...authOptions,
				allowedPhonePrefixes: [...normalizedPrefixes],
			})
			message.success('Authentication options saved successfully')
		} catch (error: any) {
			console.error('Failed to save authentication options:', error)
			message.error(error?.response?.data?.message || 'Failed to save authentication options')
		} finally {
			setSaving(false)
		}
	}

	const isDirty =
		authOptions.phoneLoginEnabled !== initialAuthOptions.phoneLoginEnabled ||
		authOptions.googleLoginEnabled !== initialAuthOptions.googleLoginEnabled ||
		authOptions.appleLoginEnabled !== initialAuthOptions.appleLoginEnabled ||
		authOptions.allowManualOtpEntry !== initialAuthOptions.allowManualOtpEntry ||
		!arePrefixListsEqual(authOptions.allowedPhonePrefixes, initialAuthOptions.allowedPhonePrefixes)

	const isDefaultPrefixes = arePrefixListsEqual(
		authOptions.allowedPhonePrefixes,
		DEFAULT_ALLOWED_PHONE_PREFIXES
	)

	const allDisabled =
		!authOptions.phoneLoginEnabled &&
		!authOptions.googleLoginEnabled &&
		!authOptions.appleLoginEnabled

	const optionsList = [
		{
			key: 'phoneLoginEnabled' as const,
			title: 'Phone Number (SMS OTP)',
			description:
				'Allows users to sign in using their registered mobile phone number and a 6-digit one-time SMS verification code.',
			icon: <PhoneOutlined style={{ fontSize: 22, color: '#1890ff' }} />,
			iconBg: '#e6f7ff',
			tagText: 'Phone / SMS',
			tagColor: 'blue',
			platforms: 'Android & iOS',
			enabled: authOptions.phoneLoginEnabled,
		},
		{
			key: 'googleLoginEnabled' as const,
			title: 'Google Sign-In',
			description:
				'Allows users to quickly and securely authenticate using their Google Account via Firebase Google Sign-In.',
			icon: <GoogleOutlined style={{ fontSize: 22, color: '#ea4335' }} />,
			iconBg: '#fce8e6',
			tagText: 'OAuth 2.0',
			tagColor: 'red',
			platforms: 'Android & iOS',
			enabled: authOptions.googleLoginEnabled,
		},
		{
			key: 'appleLoginEnabled' as const,
			title: 'Apple Sign-In',
			description:
				'Allows users to sign in using their Apple ID with Face ID, Touch ID, or passcode. Essential for Apple App Store compliance.',
			icon: <AppleOutlined style={{ fontSize: 22, color: '#1f1f1f' }} />,
			iconBg: '#f0f0f0',
			tagText: 'Apple ID',
			tagColor: 'default',
			platforms: 'iOS & Android',
			enabled: authOptions.appleLoginEnabled,
		},
	]

	return (
		<div style={{ maxWidth: 1000 }}>
			<Spin spinning={loading}>
				<Card
					title={
						<Space align='center' size={10}>
							<SafetyCertificateOutlined style={{ fontSize: 20, color: primary }} />
							<span style={{ fontSize: 16, fontWeight: 600 }}>Mobile Authentication Options</span>
						</Space>
					}
					bordered={false}
					style={{
						borderRadius: 12,
						boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
						marginBottom: 24,
					}}
					extra={
						<Tag color='processing' icon={<CheckCircleOutlined />}>
							Live Configuration
						</Tag>
					}
				>
					<Paragraph type='secondary' style={{ marginBottom: 20, fontSize: 14 }}>
						Configure which sign-in options are available on the EDC Mobile app login screen. Disabling an option immediately hides it from mobile users upon application launch or configuration refresh.
					</Paragraph>

					{allDisabled && (
						<Alert
							type='warning'
							showIcon
							message='All login methods are currently disabled'
							description='Employees will not be able to log in to the EDC Mobile app. Please keep at least one authentication method enabled.'
							style={{ marginBottom: 24, borderRadius: 8 }}
						/>
					)}

					<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
						{optionsList.map((item) => (
							<Card
								key={item.key}
								type='inner'
								bordered
								style={{
									borderRadius: 10,
									borderColor: item.enabled ? '#d9d9d9' : '#f0f0f0',
									backgroundColor: item.enabled ? '#ffffff' : '#fafafa',
									transition: 'all 0.2s ease',
								}}
								bodyStyle={{ padding: '18px 24px' }}
							>
								<Row align='middle' justify='space-between' gutter={[16, 16]}>
									<Col flex='auto'>
										<Space align='start' size={16}>
											<div
												style={{
													width: 44,
													height: 44,
													borderRadius: 10,
													backgroundColor: item.iconBg,
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													flexShrink: 0,
												}}
											>
												{item.icon}
											</div>
											<div>
												<Space size={8} wrap style={{ marginBottom: 4 }}>
													<Title level={5} style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
														{item.title}
													</Title>
													<Tag color={item.tagColor} style={{ borderRadius: 4 }}>
														{item.tagText}
													</Tag>
													<Tag style={{ borderRadius: 4, color: '#8c8c8c' }}>
														{item.platforms}
													</Tag>
												</Space>
												<Paragraph
													type='secondary'
													style={{
														margin: 0,
														fontSize: 13,
														lineHeight: 1.5,
														maxWidth: 620,
													}}
												>
													{item.description}
												</Paragraph>
											</div>
										</Space>
									</Col>
									<Col>
										<Space direction='vertical' align='end' size={2}>
											<Switch
												checked={item.enabled}
												onChange={(checked) => handleToggle(item.key, checked)}
												checkedChildren='Enabled'
												unCheckedChildren='Disabled'
											/>
											<Text
												type='secondary'
												style={{
													fontSize: 12,
													color: item.enabled ? '#52c41a' : '#8c8c8c',
												}}
											>
												{item.enabled ? 'Active' : 'Disabled'}
											</Text>
										</Space>
									</Col>
								</Row>

								{item.key === 'phoneLoginEnabled' && (
									<>
										<Divider style={{ margin: '16px 0 14px 0' }} />
										<div style={{ paddingLeft: 60 }}>
											<Row align='middle' justify='space-between' gutter={[12, 8]}>
												<Col flex='auto'>
													<Text strong style={{ fontSize: 13, display: 'block' }}>
														Allow manual OTP entry
													</Text>
													<Paragraph
														type='secondary'
														style={{ margin: '4px 0 0 0', fontSize: 12, lineHeight: 1.45, maxWidth: 560 }}
													>
														When off, the code is filled only from the SMS on this phone. When on, the user can type the 6-digit code, including when the SIM is in another phone. SMS auto-fill still works.
													</Paragraph>
												</Col>
												<Col>
													<Switch
														checked={authOptions.allowManualOtpEntry}
														disabled={!item.enabled}
														onChange={(checked) =>
															setAuthOptions((prev) => ({
																...prev,
																allowManualOtpEntry: checked,
															}))
														}
														checkedChildren='On'
														unCheckedChildren='Off'
													/>
												</Col>
											</Row>

											<Divider style={{ margin: '14px 0' }} />

											<Row
												align='middle'
												justify='space-between'
												gutter={[12, 8]}
												style={{ marginBottom: 8 }}
											>
												<Col>
													<Space size={8} align='center' wrap>
														<Text strong style={{ fontSize: 13 }}>
															Allowed Phone Prefixes (Whitelist)
														</Text>
														<Tag color='blue' style={{ borderRadius: 4, margin: 0 }}>
															{authOptions.allowedPhonePrefixes.length} prefixes
														</Tag>
													</Space>
												</Col>
												<Col>
													<Tooltip title='Reset to default Smart Axiata prefixes (010, 015, 016, 069, 070, 081, 086, 087, 093, 096, 098)'>
														<Button
															size='small'
															icon={<ReloadOutlined />}
															onClick={handleResetPrefixes}
															disabled={!item.enabled || isDefaultPrefixes}
															style={{ borderRadius: 6 }}
														>
															Reset Prefixes
														</Button>
													</Tooltip>
												</Col>
											</Row>

											<Paragraph
												type='secondary'
												style={{ margin: '0 0 10px 0', fontSize: 12, lineHeight: 1.45 }}
											>
												Only phone numbers starting with these prefixes are permitted to request SMS OTP on the mobile app. Select or type any prefix (e.g. <code>012</code>, <code>097</code>, <code>098</code>) to add it, or click Reset Prefixes to restore the default list.
											</Paragraph>

											<Row gutter={[8, 8]} align='middle'>
												<Col flex='auto'>
													<Select
														mode='tags'
														disabled={!item.enabled}
														style={{ width: '100%' }}
														placeholder='Select or type phone prefixes (e.g. 010, 012, 098)...'
														value={authOptions.allowedPhonePrefixes}
														onChange={handlePrefixesChange}
														tokenSeparators={[',', ' ', ';']}
														options={SUGGESTED_CAMBODIA_PREFIXES}
													/>
												</Col>
												<Col>
													<Space.Compact>
														<Input
															placeholder='e.g. 012'
															value={customPrefixInput}
															disabled={!item.enabled}
															onChange={(e) => setCustomPrefixInput(e.target.value)}
															onPressEnter={handleAddCustomPrefix}
															style={{ width: 100 }}
															maxLength={6}
														/>
														<Button
															icon={<PlusOutlined />}
															disabled={!item.enabled || !customPrefixInput.trim()}
															onClick={handleAddCustomPrefix}
														>
															Add
														</Button>
													</Space.Compact>
												</Col>
											</Row>
										</div>
									</>
								)}
							</Card>
						))}
					</div>

					<Divider style={{ margin: '24px 0 20px 0' }} />

					<Row justify='space-between' align='middle'>
						<Col>
							<Button
								icon={<ReloadOutlined />}
								onClick={handleReset}
								disabled={saving}
								style={{ borderRadius: 6 }}
							>
								Reset to Defaults
							</Button>
						</Col>
						<Col>
							<Space size={12}>
								<Button
									onClick={loadSettings}
									disabled={saving || !isDirty}
									style={{ borderRadius: 6 }}
								>
									Cancel
								</Button>
								<Button
									type='primary'
									icon={<SaveOutlined />}
									onClick={handleSave}
									loading={saving}
									disabled={!isDirty}
									style={{ borderRadius: 6 }}
								>
									Save Changes
								</Button>
							</Space>
						</Col>
					</Row>
				</Card>
			</Spin>
		</div>
	)
}

export default AuthOptionsTab

